import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { 
  Gift, Star, Award, Crown, Coins, 
  ChevronRight, TrendingUp, History,
  ShoppingBag, Sparkles, Car, Percent
} from 'lucide-react';

const REWARDS = [
  {
    id: 'reward-1',
    name: 'Lavage CLEAN Offert',
    description: 'Un lavage CLEAN gratuit pour votre véhicule',
    points_required: 250,
    icon: Car,
    color: 'text-blue-400',
    bgColor: 'bg-blue-500/10'
  },
  {
    id: 'reward-2',
    name: '-10% sur votre facture',
    description: 'Réduction de 10% sur votre prochaine prestation',
    points_required: 500,
    icon: Percent,
    color: 'text-green-400',
    bgColor: 'bg-green-500/10'
  },
  {
    id: 'reward-3',
    name: 'Lavage VIP Offert',
    description: 'Un lavage VIP complet gratuit',
    points_required: 750,
    icon: Star,
    color: 'text-yellow-400',
    bgColor: 'bg-yellow-500/10'
  },
  {
    id: 'reward-4',
    name: '-20% sur votre facture',
    description: 'Réduction de 20% sur votre prochaine prestation',
    points_required: 1000,
    icon: Percent,
    color: 'text-purple-400',
    bgColor: 'bg-purple-500/10'
  },
  {
    id: 'reward-5',
    name: 'Lavage PRESTIGE Offert',
    description: 'Notre lavage premium offert',
    points_required: 1500,
    icon: Crown,
    color: 'text-red-500',
    bgColor: 'bg-red-500/10'
  },
  {
    id: 'reward-6',
    name: 'Bon de 50€',
    description: 'Un bon de 50€ à utiliser sur tous nos services',
    points_required: 2500,
    icon: Gift,
    color: 'text-pink-400',
    bgColor: 'bg-pink-500/10'
  },
];

const TIERS = [
  { name: 'Bronze', min: 0, max: 499, color: 'text-amber-600', icon: Award },
  { name: 'Argent', min: 500, max: 1499, color: 'text-zinc-400', icon: Star },
  { name: 'Or', min: 1500, max: 2999, color: 'text-yellow-400', icon: Crown },
  { name: 'Platine', min: 3000, max: Infinity, color: 'text-purple-400', icon: Sparkles },
];

export default function LoyaltyPage() {
  const { user } = useAuth();
  const [loyaltyData, setLoyaltyData] = useState({ points: 0, history: [] });
  const API_URL = process.env.REACT_APP_BACKEND_URL;

  useEffect(() => {
    if (user) {
      fetchLoyaltyData();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  const fetchLoyaltyData = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/loyalty`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setLoyaltyData(data);
      }
    } catch (error) {
      console.error('Error fetching loyalty data:', error);
    }
  };

  const currentPoints = user ? (loyaltyData.points || user.loyalty_points || 0) : 0;
  const currentTier = TIERS.find(t => currentPoints >= t.min && currentPoints <= t.max) || TIERS[0];
  const nextTier = TIERS[TIERS.indexOf(currentTier) + 1];
  const progressToNext = nextTier 
    ? ((currentPoints - currentTier.min) / (nextTier.min - currentTier.min)) * 100 
    : 100;

  return (
    <div className="min-h-screen bg-[#09090B]">
      {/* Hero */}
      <section className="bg-gradient-to-b from-zinc-900 to-[#09090B] py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <div className="inline-flex items-center gap-2 bg-yellow-600/20 text-yellow-400 px-4 py-2 rounded-full border border-yellow-600/30 mb-6">
            <Gift size={18} />
            <span className="text-sm font-medium">Programme de fidélité</span>
          </div>
          <h1 className="text-5xl font-bold text-white mb-4">
            ESPACE <span className="text-red-500">FIDÉLITÉ</span>
          </h1>
          <p className="text-xl text-zinc-400 max-w-2xl mx-auto">
            Cumulez des points à chaque achat et profitez de récompenses exclusives
          </p>
          <p className="text-lg text-yellow-400 mt-4 font-medium">
            <Coins className="inline mr-2" size={20} />
            1€ dépensé = 1 point gagné
          </p>
        </div>
      </section>

      {/* User Status or CTA */}
      <section className="py-12 border-b border-zinc-800">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {user ? (
            <Card className="bg-gradient-to-r from-zinc-900 to-zinc-800 border-zinc-700 overflow-hidden">
              <CardContent className="p-8">
                <div className="grid md:grid-cols-3 gap-8 items-center">
                  {/* Points */}
                  <div className="text-center md:text-left">
                    <p className="text-zinc-400 text-sm mb-1">Vos points</p>
                    <p className="text-5xl font-bold text-white">{currentPoints}</p>
                    <p className="text-yellow-400 text-sm mt-1">points disponibles</p>
                  </div>
                  
                  {/* Tier */}
                  <div className="text-center">
                    <currentTier.icon className={`${currentTier.color} mx-auto mb-2`} size={48} />
                    <p className={`text-2xl font-bold ${currentTier.color}`}>Niveau {currentTier.name}</p>
                    {nextTier && (
                      <div className="mt-4">
                        <div className="flex justify-between text-xs text-zinc-400 mb-1">
                          <span>{currentTier.name}</span>
                          <span>{nextTier.name}</span>
                        </div>
                        <div className="h-2 bg-zinc-700 rounded-full overflow-hidden">
                          <div 
                            className="h-full bg-gradient-to-r from-yellow-500 to-yellow-400 transition-all duration-500"
                            style={{ width: `${progressToNext}%` }}
                          />
                        </div>
                        <p className="text-zinc-500 text-xs mt-1">
                          Encore {nextTier.min - currentPoints} points pour le niveau {nextTier.name}
                        </p>
                      </div>
                    )}
                  </div>
                  
                  {/* Actions */}
                  <div className="text-center md:text-right space-y-3">
                    <Link to="/reservation">
                      <Button className="bg-red-600 hover:bg-red-700 w-full md:w-auto">
                        <TrendingUp size={18} className="mr-2" />
                        Gagner des points
                      </Button>
                    </Link>
                    <p className="text-zinc-500 text-sm">
                      Réservez une prestation pour cumuler des points
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>
          ) : (
            <Card className="bg-zinc-900/50 border-zinc-800">
              <CardContent className="p-8 text-center">
                <Gift className="mx-auto text-yellow-400 mb-4" size={48} />
                <h2 className="text-2xl font-bold text-white mb-2">Rejoignez notre programme</h2>
                <p className="text-zinc-400 mb-6">
                  Créez un compte pour commencer à cumuler des points et profiter de récompenses exclusives
                </p>
                <div className="flex justify-center gap-4">
                  <Link to="/inscription">
                    <Button className="bg-red-600 hover:bg-red-700">
                      Créer un compte
                    </Button>
                  </Link>
                  <Link to="/connexion">
                    <Button variant="outline" className="border-zinc-600 text-white hover:bg-zinc-800">
                      Se connecter
                    </Button>
                  </Link>
                </div>
              </CardContent>
            </Card>
          )}
        </div>
      </section>

      {/* How it works */}
      <section className="py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl font-bold text-white text-center mb-12">COMMENT ÇA MARCHE ?</h2>
          
          <div className="grid md:grid-cols-3 gap-8">
            <Card className="bg-zinc-900/50 border-zinc-800 text-center">
              <CardContent className="p-6">
                <div className="w-16 h-16 bg-red-500/10 rounded-full flex items-center justify-center mx-auto mb-4">
                  <ShoppingBag className="text-red-500" size={32} />
                </div>
                <h3 className="text-xl font-bold text-white mb-2">1. Achetez</h3>
                <p className="text-zinc-400">
                  Chaque euro dépensé vous rapporte 1 point de fidélité
                </p>
              </CardContent>
            </Card>
            
            <Card className="bg-zinc-900/50 border-zinc-800 text-center">
              <CardContent className="p-6">
                <div className="w-16 h-16 bg-yellow-500/10 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Coins className="text-yellow-400" size={32} />
                </div>
                <h3 className="text-xl font-bold text-white mb-2">2. Cumulez</h3>
                <p className="text-zinc-400">
                  Vos points s'accumulent automatiquement sur votre compte
                </p>
              </CardContent>
            </Card>
            
            <Card className="bg-zinc-900/50 border-zinc-800 text-center">
              <CardContent className="p-6">
                <div className="w-16 h-16 bg-green-500/10 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Gift className="text-green-400" size={32} />
                </div>
                <h3 className="text-xl font-bold text-white mb-2">3. Profitez</h3>
                <p className="text-zinc-400">
                  Échangez vos points contre des récompenses exclusives
                </p>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Rewards */}
      <section className="py-16 bg-zinc-900/30 border-t border-zinc-800">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl font-bold text-white text-center mb-4">RÉCOMPENSES DISPONIBLES</h2>
          <p className="text-zinc-400 text-center mb-12">Échangez vos points contre ces avantages exclusifs</p>
          
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {REWARDS.map((reward) => {
              const canRedeem = user && currentPoints >= reward.points_required;
              return (
                <Card 
                  key={reward.id}
                  className={`bg-zinc-900/50 border-zinc-800 overflow-hidden transition-all ${
                    canRedeem ? 'hover:border-green-500/50' : ''
                  }`}
                  data-testid={`reward-${reward.id}`}
                >
                  <div className={`h-1 ${reward.bgColor}`} />
                  <CardContent className="p-6">
                    <div className="flex items-start gap-4">
                      <div className={`w-14 h-14 ${reward.bgColor} rounded-xl flex items-center justify-center flex-shrink-0`}>
                        <reward.icon className={reward.color} size={28} />
                      </div>
                      <div className="flex-1">
                        <h3 className="text-lg font-bold text-white mb-1">{reward.name}</h3>
                        <p className="text-zinc-400 text-sm mb-3">{reward.description}</p>
                        <div className="flex items-center justify-between">
                          <span className="text-yellow-400 font-bold">{reward.points_required} pts</span>
                          {user ? (
                            canRedeem ? (
                              <Button size="sm" className="bg-green-600 hover:bg-green-700">
                                Échanger
                              </Button>
                            ) : (
                              <span className="text-zinc-500 text-sm">
                                -{reward.points_required - currentPoints} pts
                              </span>
                            )
                          ) : (
                            <Link to="/connexion">
                              <Button size="sm" variant="outline" className="border-zinc-700 text-zinc-400">
                                Connectez-vous
                              </Button>
                            </Link>
                          )}
                        </div>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </div>
      </section>

      {/* Tiers */}
      <section className="py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl font-bold text-white text-center mb-4">NIVEAUX DE FIDÉLITÉ</h2>
          <p className="text-zinc-400 text-center mb-12">Plus vous cumulez de points, plus vos avantages augmentent</p>
          
          <div className="grid md:grid-cols-4 gap-6">
            {TIERS.map((tier, idx) => (
              <Card 
                key={tier.name}
                className={`bg-zinc-900/50 border-zinc-800 text-center ${
                  user && currentTier.name === tier.name ? 'ring-2 ring-yellow-500/50 border-yellow-500/30' : ''
                }`}
              >
                <CardContent className="p-6">
                  <tier.icon className={`${tier.color} mx-auto mb-3`} size={40} />
                  <h3 className={`text-xl font-bold ${tier.color} mb-2`}>{tier.name}</h3>
                  <p className="text-zinc-400 text-sm mb-4">
                    {tier.max === Infinity ? `${tier.min}+ pts` : `${tier.min} - ${tier.max} pts`}
                  </p>
                  <ul className="text-zinc-400 text-sm space-y-1">
                    <li>✓ Cumul de points</li>
                    {idx >= 1 && <li>✓ -5% supplémentaire</li>}
                    {idx >= 2 && <li>✓ Offres exclusives</li>}
                    {idx >= 3 && <li>✓ Service prioritaire</li>}
                  </ul>
                  {user && currentTier.name === tier.name && (
                    <div className="mt-4 bg-yellow-500/20 text-yellow-400 text-xs py-1 px-3 rounded-full inline-block">
                      Votre niveau
                    </div>
                  )}
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-16 bg-zinc-900/50 border-t border-zinc-800">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl font-bold text-white mb-4">PRÊT À CUMULER DES POINTS ?</h2>
          <p className="text-zinc-400 mb-8">
            Réservez votre prochaine prestation et commencez à profiter de nos avantages fidélité
          </p>
          <div className="flex justify-center gap-4">
            <Link to="/reservation">
              <Button size="lg" className="bg-red-600 hover:bg-red-700 btn-glow">
                Réserver maintenant
                <ChevronRight size={18} className="ml-1" />
              </Button>
            </Link>
            <Link to="/services">
              <Button size="lg" variant="outline" className="border-zinc-600 text-white hover:bg-zinc-800">
                Voir nos services
              </Button>
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
